import { TokenAutomaticRenewMode } from '@axa-fr/react-oidc';
import {
  AuthErrorBoundary,
  CallbackSuccess,
  CenteredLoading,
  clearAllUserSession,
  SessionLost,
} from '@healthhub/react-ui';

import { LayoutViewBridge } from '@shared/context';
import { persistor } from '@shared/stores';
import { clearCookies } from '@shared/utils/cookie';

import { OidcLocation } from './OidcLocation';
import { authAuthority, authClientId, authClientSecret, authScope, envBaseUrl } from '../configs/config';

class InMemoryStorage implements Storage {
  get length(): number {
    return Object.keys(this.store).length;
  }

  private store: Record<string, string> = {};

  clear(): void {
    this.store = {};
    sessionStorage.clear();
  }

  getItem(key: string): null | string {
    if (key === 'oidc.default') {
      return this.store[key] ?? sessionStorage.getItem(key) ?? null;
    }

    return sessionStorage.getItem(key) ?? null;
  }

  key(index: number): null | string {
    return sessionStorage.key(index) ?? (Object.keys(this.store)[index] || null);
  }

  removeItem(key: string): void {
    delete this.store[key];
    delete sessionStorage[key];
  }

  setItem(key: string, value: string): void {
    if (key === 'oidc.default') {
      try {
        const tokenConfig = JSON.parse(value);
        if (tokenConfig?.tokens?.accessToken) {
          sessionStorage.setItem(
            key,
            JSON.stringify({
              ...tokenConfig,
              tokens: {
                ...tokenConfig.tokens,
                accessToken: null,
                accessTokenPayload: null,
              },
            })
          );
          this.store[key] = value;
        } else {
          sessionStorage.setItem(key, value);
        }
      } catch (error) {
        console.warn("Error parsing 'oidc.default' token config, storing in sessionStorage only", error);
        sessionStorage.setItem(key, value);
      }
    } else {
      sessionStorage.setItem(key, value);
    }
  }
}

const memoryStorage = new InMemoryStorage();

const storageProxy = new Proxy(memoryStorage, {
  get: (target, prop: string) => target.getItem(prop),
  set: (target, prop: string, value) => {
    target.setItem(prop, value);
    return true;
  },
});

export const AUTH_CONFIG = {
  authenticatingComponent: CenteredLoading,
  authenticatingErrorComponent: () => <AuthErrorBoundary layoutView={LayoutViewBridge.getLayoutViewBridge?.()} />,
  callbackSuccessComponent: CallbackSuccess,
  configuration: {
    authority: authAuthority(),
    client_id: authClientId(),
    redirect_uri: `${envBaseUrl()}/callback`,
    refresh_time_before_tokens_expiration_in_second: 0,
    scope: authScope(),
    service_worker_only: false,
    storage: storageProxy,
    token_automatic_renew_mode: TokenAutomaticRenewMode.AutomaticOnlyWhenFetchExecuted,
    token_renew_mode: 'id_token_invalid',
    token_request_extras: {
      client_secret: authClientSecret(),
    },
  },
  loadingComponent: CenteredLoading,
  location: new OidcLocation(),
  sessionLostComponent: () => (
    <SessionLost
      clearAllUserSession={() => clearAllUserSession({ persistor, clearCookies })}
      layoutView={LayoutViewBridge.getLayoutViewBridge?.()}
    />
  ),
};
